import { useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";

export const TimelineDialog = ({ open, onOpenChange, uid, timelines, initial, defaultParent, onSaved }) => {
  const editing = !!initial?.id;
  const [form, setForm] = useState({
    name: initial?.name || "", description: initial?.description || "",
    parent_timeline_id: initial?.parent_timeline_id || defaultParent || "none",
    branch_point_timestamp: initial?.branch_point_timestamp ?? "",
  });
  const [busy, setBusy] = useState(false);
  const [rate, setRate] = useState({ here: initial?.time_rate?.here ?? 1, prime: initial?.time_rate?.prime ?? 1 });
  const [reason, setReason] = useState("");
  const [suggesting, setSuggesting] = useState(false);
  const suggest = async () => {
    setSuggesting(true);
    try {
      const { data } = await api.post(`/timelines/${initial.id}/ai/suggest-rate`);
      setRate({ here: data.here, prime: data.prime });
      setReason(data.reason);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setSuggesting(false);
    }
  };
  const isBranch = form.parent_timeline_id !== "none";

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const bp = form.branch_point_timestamp === "" ? null : parseInt(form.branch_point_timestamp, 10);
    try {
      const { data } = editing
        ? await api.patch(`/timelines/${initial.id}`, { name: form.name, description: form.description, branch_point_timestamp: initial.parent_timeline_id ? bp : null, time_rate: { here: Number(rate.here) || 1, prime: Number(rate.prime) || 1 } })
        : await api.post(`/universes/${uid}/timelines`, { name: form.name, description: form.description, parent_timeline_id: isBranch ? form.parent_timeline_id : null, branch_point_timestamp: isBranch ? bp : null });
      onSaved(data);
      onOpenChange(false);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="timeline-dialog">
        <form onSubmit={save}>
          <DialogHeader><DialogTitle className="font-display text-3xl">{editing ? "Edit timeline" : "New timeline"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-6">
            <div className="space-y-2"><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="The Ember Dominion" required data-testid="timeline-name-input" /></div>
            <div className="space-y-2"><Label>Description</Label><Textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What if Kael won the Siege?" data-testid="timeline-description-input" /></div>
            {!editing && (
              <div className="space-y-2">
                <Label>Branches from</Label>
                <Select value={form.parent_timeline_id} onValueChange={(v) => setForm({ ...form, parent_timeline_id: v })}>
                  <SelectTrigger data-testid="timeline-parent-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Independent reality —</SelectItem>
                    {timelines.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {editing && (
              <div className="space-y-2 rounded-md border border-[#8B5CF6]/30 bg-[#8B5CF6]/5 p-3" data-testid="time-rate-section">
                <div className="flex items-center justify-between">
                  <Label>Time rate vs Prime</Label>
                  <Button type="button" size="sm" variant="ghost" className="text-[#C4B5FD]" onClick={suggest} disabled={suggesting} data-testid="suggest-rate-button">{suggesting ? "Reading the lore…" : "✦ Suggest from lore"}</Button>
                </div>
                <div className="flex items-center gap-2 text-sm text-[#E8DFD1]">
                  <Input type="number" step="any" min="0" className="w-24 h-9 font-mono-lf" value={rate.here} onChange={(e) => setRate({ ...rate, here: e.target.value })} data-testid="rate-here-input" />
                  <span>here =</span>
                  <Input type="number" step="any" min="0" className="w-24 h-9 font-mono-lf" value={rate.prime} onChange={(e) => setRate({ ...rate, prime: e.target.value })} data-testid="rate-prime-input" />
                  <span>in Prime</span>
                </div>
                {reason && <p className="text-xs italic text-[#C4B5FD]" data-testid="rate-reason">{reason} Review it, then save to accept.</p>}
              </div>
            )}
            {(editing ? !!initial.parent_timeline_id : isBranch) && (
              <div className="space-y-2">
                <Label>Divergence timestamp</Label>
                <Input type="number" className="font-mono-lf" value={form.branch_point_timestamp} onChange={(e) => setForm({ ...form, branch_point_timestamp: e.target.value })} required data-testid="timeline-branch-point-input" />
                <p className="text-xs text-[#A8A090]">Events from the parent up to this moment are inherited.</p>
              </div>
            )}
          </div>
          <DialogFooter><Button type="submit" disabled={busy} data-testid="timeline-save-button">{busy ? "Saving…" : "Save timeline"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
